import React, { useState, useEffect } from 'react';
import { Check, ShoppingCart, Search, X, Star, Award, TrendingUp, DollarSign } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const TireShop = () => {
  const [tires, setTires] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { addToCart, calculateFinalPrice } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    loadTires();
  }, []);

  const loadTires = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tires`);
      setTires(response.data || []);
    } catch (error) {
      console.error('Error loading tires:', error);
      toast.error('Failed to load tires. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToCart = (tire) => {
    const finalPrice = calculateFinalPrice(tire, false);
    addToCart({ ...tire, final_price: finalPrice }, 1);
    toast.success('Added to cart!', {
      description: `${tire.size} - ${tire.brand}`,
      action: {
        label: 'View Cart',
        onClick: () => navigate('/cart')
      }
    });
  };

  const renderDiscount = (tire) => {
    if (!tire.discount_type || tire.discount_type === 'none' || !tire.discount_value) {
      return null;
    }

    const now = new Date();
    const saleStart = tire.sale_start_date ? new Date(tire.sale_start_date) : null;
    const saleEnd = tire.sale_end_date ? new Date(tire.sale_end_date) : null;
    const isSaleActive = (!saleStart || now >= saleStart) && (!saleEnd || now <= saleEnd);

    if (!isSaleActive) return null;

    const finalPrice = calculateFinalPrice(tire, false);
    const savings = tire.price - finalPrice;

    return (
      <div className="mt-2">
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-green-600">${finalPrice.toFixed(2)}</span>
          <span className="text-sm line-through text-gray-500">${tire.price.toFixed(2)}</span>
        </div>
        <Badge className="bg-red-100 text-red-800 text-xs mt-1">
          Save ${savings.toFixed(2)}
        </Badge>
      </div>
    );
  };

  if (isLoading) {
    return (
      <section id="tires" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-gray-600">Loading tires...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="tires" className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Premium Tire Selection</h2>
          <p className="text-lg text-gray-600">Quality tires at competitive prices with fast delivery</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tires.map((tire) => {
            const finalPrice = calculateFinalPrice(tire, false);
            const hasDiscount = finalPrice < tire.price;

            return (
              <Card key={tire.size} className="overflow-hidden hover:shadow-lg transition-shadow">
                {tire.photo_url && (
                  <div className="w-full h-48 bg-gray-100 overflow-hidden">
                    <img
                      src={`${BACKEND_URL}${tire.photo_url}`}
                      alt={`${tire.brand} ${tire.size}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                <div className="p-4">
                  {/* Badges */}
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {tire.is_featured && (
                      <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                        <Award className="w-3 h-3 mr-1" />
                        Featured
                      </Badge>
                    )}
                    {tire.is_best_seller && (
                      <Badge className="bg-orange-100 text-orange-800 border-orange-200">
                        <TrendingUp className="w-3 h-3 mr-1" />
                        Best Seller
                      </Badge>
                    )}
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{tire.size}</h3>
                  <p className="text-sm text-gray-600 mb-2">{tire.brand}</p>
                  
                  {/* Category Badge */}
                  <Badge className="bg-blue-100 text-blue-800 mb-3">{tire.category}</Badge>

                  {/* Rating */}
                  {tire.star_rating && (
                    <div className="flex items-center gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < Math.floor(tire.star_rating)
                              ? 'text-yellow-500 fill-yellow-500'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                      <span className="text-sm text-gray-600 ml-1">({tire.star_rating})</span>
                    </div>
                  )}

                  {/* Treadwear */}
                  {tire.treadwear_rating && (
                    <p className="text-xs text-gray-500 mb-3">
                      Treadwear: {tire.treadwear_rating}
                    </p>
                  )}

                  {/* Price */}
                  <div className="mb-4">
                    {hasDiscount ? (
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-bold text-green-600">
                            ${finalPrice.toFixed(2)}
                          </span>
                          <span className="text-sm line-through text-gray-500">
                            ${tire.price.toFixed(2)}
                          </span>
                        </div>
                        <Badge className="bg-red-100 text-red-800 text-xs mt-1">
                          <DollarSign className="w-3 h-3 mr-1" />
                          Save ${(tire.price - finalPrice).toFixed(2)}
                        </Badge>
                      </div>
                    ) : (
                      <span className="text-2xl font-bold text-gray-900">
                        ${tire.price.toFixed(2)}
                      </span>
                    )}
                  </div>

                  {/* Stock Status */}
                  <div className="mb-4">
                    <span
                      className={`text-sm font-medium ${
                        tire.stock_status === 'In Stock'
                          ? 'text-green-600'
                          : tire.stock_status === 'Low Stock'
                          ? 'text-orange-600'
                          : 'text-red-600'
                      }`}
                    >
                      {tire.stock_status}
                    </span>
                  </div>

                  {/* Add to Cart Button */}
                  <Button
                    onClick={() => handleAddToCart(tire)}
                    disabled={tire.stock_status === 'Out of Stock'}
                    className="w-full bg-red-600 hover:bg-red-700"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Add to Cart
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        {tires.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600">No tires available at the moment.</p>
          </div>
        )}
      </div>
    </section>
  );
};